import { useEffect, useMemo, useState } from "react";
import { Card } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { endpointPrincipal } from "../../api/endPoints/endpointPrincipal/endpointPrincipal";

import ContainerTitulo from "../../components/Formularios/Container/ContainerTitulo";
import { showConfirmationDialog } from "../../components/Formularios/Modal/ModalExcluir";
import ModalLoading from "../../components/Formularios/Modal/ModalLoading";
import ActionToolbar from "../../components/ActionToolbar/ActionToolbar";
import CustomTable, { ColunaTabela } from "../../components/Formularios/Table/CustomTable";
import ModalFiltro, { CampoPesquisa } from "../../components/Modais/ModalFiltro";
import ModalPreferenciasColunas from "../../components/Modais/ModalPreferenciasColunas";
import usePreferenciasColunas from "../../hooks/usePreferenciasColunas";

// Interface genérica para os dados
interface ItemListagem {
  [key: string]: any;
}

// Props para configurar o componente de listagem
interface ListagemPadraoProps<T extends ItemListagem> {
  titulo: string;
  // Identificador da tela para salvar as preferências do grid
  tela: string;
  endpoint: string;
  // Deve ser uma constante fora do componente (referência estável)
  colunas: ColunaTabela<T>[];
  campoId: keyof T;
  rotaBase: string;
  camposPesquisa: CampoPesquisa<T>[];
  usaPaginacaoServidor?: boolean;
  itensPorPaginaPadrao?: number;
  mensagemConfirmacaoExclusao?: (item: T) => string;
  parametroExclusao?: (item: T) => string | number;
}

export function ListagemPadrao<T extends ItemListagem>({
  titulo,
  tela,
  endpoint,
  colunas,
  campoId,
  rotaBase,
  camposPesquisa,
  usaPaginacaoServidor = false,
  itensPorPaginaPadrao = 10,
  mensagemConfirmacaoExclusao,
  parametroExclusao,
}: ListagemPadraoProps<T>) {
  const navigate = useNavigate();

  const [itens, setItens] = useState<T[]>([]);
  const [isCarregando, setIsCarregando] = useState(false);
  const [filtrosAplicados, setFiltrosAplicados] = useState<Record<string, string>>({});
  const [modalFiltroAberto, setModalFiltroAberto] = useState(false);

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPaginasServidor, setTotalPaginasServidor] = useState(1);

  const {
    colunasVisiveis,
    colunasOrdenadas,
    itensPorPagina,
    modalAberto: modalPreferenciasAberto,
    abrirModal: abrirModalPreferencias,
    fecharModal: fecharModalPreferencias,
    salvarPreferencias,
    resetarPreferencias,
    carregado: preferenciasCarregadas,
  } = usePreferenciasColunas<T>(tela, colunas, itensPorPaginaPadrao);

  const filtrosAtivos = Object.values(filtrosAplicados).some((valor) => valor.trim() !== "");

  // Ações CRUD
  const excluirItem = async (item: T) => {
    const itemId = parametroExclusao ? parametroExclusao(item) : item[campoId];
    const mensagem = mensagemConfirmacaoExclusao
      ? mensagemConfirmacaoExclusao(item)
      : `Tem certeza que deseja excluir este registro?`;

    await showConfirmationDialog(
      "Excluir Registro",
      mensagem,
      "question",
      "Excluir",
      "Cancelar"
    )
      .then((result) => {
        if (result.confirmed) {
          excluirItemPorId(itemId);
        }
      })
      .catch((erro) => {
        toast.error(
          `Erro ao excluir registro. Motivo: ${erro.response?.data?.message || erro.message}`
        );
      });
  };

  async function excluirItemPorId(id: number | string) {
    setIsCarregando(true);
    try {
      await endpointPrincipal.delete(`${endpoint}/${id}`);
      toast.success("Registro excluído com sucesso");
      await buscarDados();
    } catch (error: any) {
      toast.error(`Erro ao excluir: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsCarregando(false);
    }
  }

  async function buscarDados() {
    setIsCarregando(true);
    try {
      if (usaPaginacaoServidor) {
        const params = new URLSearchParams({
          page: String(currentPage),
          limit: String(itensPorPagina),
        });

        // Adiciona os filtros aplicados como parâmetros
        Object.entries(filtrosAplicados).forEach(([campo, valor]) => {
          if (valor.trim()) {
            params.append(campo, valor.trim());
          }
        });

        const response = await endpointPrincipal.get(`${endpoint}?${params.toString()}`);
        setItens(response.data.data || []);
        setTotalPaginasServidor(response.data.totalPages || 1);
      } else {
        const response = await endpointPrincipal.get(endpoint);
        setItens(response.data || []);
      }
    } catch (error: any) {
      toast.error(`Erro ao buscar dados: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsCarregando(false);
    }
  }

  // Paginação no cliente: busca uma única vez ao abrir a tela
  useEffect(() => {
    if (!usaPaginacaoServidor) {
      buscarDados();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usaPaginacaoServidor]);

  // Paginação no servidor: busca quando mudam página, filtros ou itens por página
  useEffect(() => {
    if (usaPaginacaoServidor && preferenciasCarregadas) {
      buscarDados();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usaPaginacaoServidor, preferenciasCarregadas, currentPage, filtrosAplicados, itensPorPagina]);

  // Filtragem no cliente: todos os filtros preenchidos precisam corresponder
  const itensFiltrados = useMemo(() => {
    if (usaPaginacaoServidor) return itens;

    const filtros = Object.entries(filtrosAplicados).filter(([, termo]) => termo.trim());
    if (!filtros.length) return itens;

    return itens.filter((item) =>
      filtros.every(([campo, termo]) => {
        const valor = item[campo];
        return (
          valor !== null &&
          valor !== undefined &&
          String(valor).toLowerCase().includes(termo.trim().toLowerCase())
        );
      })
    );
  }, [itens, filtrosAplicados, usaPaginacaoServidor]);

  const totalPaginas = usaPaginacaoServidor
    ? totalPaginasServidor
    : Math.max(1, Math.ceil(itensFiltrados.length / itensPorPagina));

  // Mantém a página atual dentro do total (ex.: após excluir o último item da página)
  useEffect(() => {
    if (!usaPaginacaoServidor && currentPage > totalPaginas) {
      setCurrentPage(totalPaginas);
    }
  }, [usaPaginacaoServidor, currentPage, totalPaginas]);

  // Obter os itens da página atual (paginação no cliente)
  const itensPaginados = usaPaginacaoServidor
    ? itens
    : itensFiltrados.slice(
        (currentPage - 1) * itensPorPagina,
        currentPage * itensPorPagina
      );

  const handleListar = () => {
    // Na paginação no servidor, voltar para a página 1 já dispara a busca
    if (usaPaginacaoServidor && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }
    setCurrentPage(1);
    buscarDados();
  };

  const handlePesquisar = (filtros: Record<string, string>) => {
    setFiltrosAplicados(filtros);
    setCurrentPage(1);
    setModalFiltroAberto(false);
  };

  const handleLimparFiltros = () => {
    setFiltrosAplicados({});
    setCurrentPage(1);
    setModalFiltroAberto(false);
  };

  return (
    <>
      <div className="container-fluid mt-2">
        <div className="title-container">
          <ContainerTitulo titulo={titulo} />
        </div>
        <Card>
          <ActionToolbar
            onIncluir={() => navigate(`${rotaBase}/novo`)}
            onFiltrar={() => setModalFiltroAberto(true)}
            filtrosAtivos={filtrosAtivos}
            customButtons={[
              { icon: <SearchIcon />, title: "Listar Registros", onClick: handleListar },
            ]}
            mostrarOrganizarColunas={true}
            onOrganizarColunas={abrirModalPreferencias}
          />

          <CustomTable<T>
            columns={colunasVisiveis}
            data={itensPaginados}
            idField={campoId}
            onVisualizar={(item) => navigate(`${rotaBase}/visu/${item[campoId]}`)}
            onEditar={(item) => navigate(`${rotaBase}/editar/${item[campoId]}`)}
            onDelete={excluirItem}
            acoesNoInicio={true}
            paginaAtual={currentPage}
            totalPaginas={totalPaginas}
            setPaginaAtual={setCurrentPage}
            carregando={isCarregando}
          />
        </Card>

        <ModalFiltro
          aberto={modalFiltroAberto}
          titulo={titulo}
          campos={camposPesquisa}
          valores={filtrosAplicados}
          onPesquisar={handlePesquisar}
          onLimpar={handleLimparFiltros}
          onFechar={() => setModalFiltroAberto(false)}
        />

        <ModalPreferenciasColunas
          aberto={modalPreferenciasAberto}
          onFechar={fecharModalPreferencias}
          colunas={colunasOrdenadas}
          itensPorPaginaAtual={itensPorPagina}
          itensPorPaginaPadrao={itensPorPaginaPadrao}
          onSalvar={({ colunas, itensPorPagina }) => {
            salvarPreferencias({ colunas, itensPorPagina });
            setCurrentPage(1);
          }}
          onResetar={() => {
            resetarPreferencias();
            setCurrentPage(1);
          }}
        />

        {/* Modal de carregamento */}
        <ModalLoading show={isCarregando} label="Carregando..." />
      </div>

      <style>
        {`
          .title-container {
            padding: 8px 0px;
            margin-bottom: 8px;
          }

          @media screen and (max-width: 412px) {
            .title-container {
              padding: 8px;
            }
          }
        `}
      </style>
    </>
  );
}
